import { CheckIcon } from '@heroicons/react/20/solid';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DropdownProps } from 'components/generic-components/dropdown/type';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const CHUNK_SIZE = 500;

const Dropdown = React.forwardRef<any, DropdownProps>(
  (
    {
      id,
      values,
      fieldDisplayName,
      name,
      required = false,
      placeholder = '',
      onSelectedItemChange,
      selectedItem,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      defaultValue = '',
      loading,
      disabled,
      position = 'bottom',
      containerClassName,
      customFilterLogic,
      autoComplete = 'off',
      minWidthForOptionLabel = 'min-w-[12px]',
      lazyLoading = false,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      adaptiveWidth = true,
      doNotClearField = false,
      clearable = false,
      icon,
      button,
      buttonPosition = 'top',
      btnClasses,
    },
    ref,
  ) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [displayedValues, setDisplayedValues] = useState(values?.slice(0, CHUNK_SIZE));
    const [fullSearchResults, setFullSearchResults] = useState([]);
    const [chunkIndex, setChunkIndex] = useState(1);

    // Scroll highlighted item into view on keyboard navigation
    useEffect(() => {
      if (hoveredIndex === null || !listRef.current) return;
      const items = listRef.current.querySelectorAll<HTMLElement>('[role="option"]');
      items[hoveredIndex]?.scrollIntoView({ block: 'nearest' });
    }, [hoveredIndex]);

    const ButtonComponent = ({ position: pos }: { position: 'top' | 'bottom' }) => {
      if (!button) return null;
      const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        (button as React.ReactElement).props.onClick?.(e);
      };
      return (
        <div
          className={`sticky ${pos === 'top' ? '-top-1' : '-bottom-1'} z-50 bg-white px-3 py-2 ${btnClasses ?? ''}`}
          onClick={handleClick}
        >
          {button}
        </div>
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
      debounce((q) => {
        const searchResults = customFilterLogic
          ? customFilterLogic(q, values)
          : values?.filter(({ label = '', value = '', additionalValue = '' }) => {
              const labelStr = typeof label === 'string' ? label : '';
              return (
                labelStr.toLowerCase().includes(q.toLowerCase()) ||
                value.toLowerCase().includes(q.toLowerCase()) ||
                additionalValue.toLowerCase().includes(q.toLowerCase())
              );
            });
        setFullSearchResults(searchResults);
        setDisplayedValues(searchResults?.slice(0, CHUNK_SIZE));
        setChunkIndex(1);
      }, 300),
      [values, customFilterLogic],
    );

    useEffect(() => {
      debouncedSearch(query);
    }, [query, debouncedSearch]);

    useEffect(() => {
      if (!query) {
        setDisplayedValues(values?.slice(0, CHUNK_SIZE));
        setChunkIndex(1);
        setFullSearchResults([]);
      }
    }, [query, values]);

    const loadNextChunk = () => {
      if (lazyLoading && fullSearchResults.length > CHUNK_SIZE * chunkIndex) {
        const nextChunkIndex = CHUNK_SIZE * chunkIndex;
        const nextChunk = fullSearchResults?.slice(nextChunkIndex, nextChunkIndex + CHUNK_SIZE);
        setDisplayedValues((prev) => [...prev, ...nextChunk]);
        setChunkIndex(chunkIndex + 1);
      }
    };

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight * 1.5) {
        loadNextChunk();
      }
    };

    let filteredValues = customFilterLogic
      ? customFilterLogic(query, displayedValues)
      : query === '' || !displayedValues || displayedValues?.length === 0
        ? displayedValues || []
        : displayedValues?.filter(({ label = '', value = '', additionalValue = '' }) => {
            const labelStr = typeof label === 'string' ? label : '';
            return (
              labelStr.toLowerCase().includes(query.toLowerCase()) ||
              value.toLowerCase().includes(query.toLowerCase()) ||
              additionalValue.toLowerCase().includes(query.toLowerCase())
            );
          });

    useEffect(() => {
      if (lazyLoading) {
        setDisplayedValues(values?.slice(0, CHUNK_SIZE));
        setChunkIndex(1);
      } else {
        setDisplayedValues(values);
      }
    }, [values, lazyLoading]);

    if (Array.isArray(filteredValues) && filteredValues?.length > 0) {
      filteredValues = filteredValues?.filter((value) => Boolean(value));
    }

    const selectedValue = values?.find((v) => v.value === selectedItem);
    const selectedLabel =
      typeof selectedValue?.label === 'string' ? (selectedValue?.label ?? selectedItem) : (selectedItem ?? '');
    const inputDisplayValue = loading
      ? 'Loading...'
      : !open
        ? selectedLabel
        : doNotClearField && !query
          ? selectedLabel
          : query;

    const showList = open && !disabled && !loading;
    const showClear = clearable && !disabled && !loading && Boolean(selectedItem);
    const hasResults = filteredValues?.length > 0;

    const handleOpenChange = (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) setQuery('');
    };

    return (
      // `modal` pushes Popover's own RemoveScroll onto react-remove-scroll's lockStack, which
      // otherwise keeps Radix Dialog's RemoveScroll on top and blocks trackpad/wheel scroll on
      // portaled popover content whose shards don't include us.
      <PopoverPrimitive.Root open={showList} onOpenChange={handleOpenChange} modal>
        <div className={containerClassName} data-disabled={disabled}>
          {fieldDisplayName && (
            <label
              className={`mb-2 block text-sm font-medium text-gray-900 ${required ? 'required' : ''}`}
              htmlFor={id}
            >
              {fieldDisplayName}
            </label>
          )}

          <PopoverPrimitive.Anchor asChild>
            <div className="relative w-full">
              {icon && (
                <div
                  className={`absolute inset-y-0 left-0 flex items-center rounded-r-md px-4 focus:outline-none ${
                    disabled ? '' : 'hover:cursor-pointer'
                  }`}
                >
                  {icon}
                </div>
              )}
              <input
                onFocus={() => !disabled && !loading && setOpen(true)}
                onClick={() => !disabled && !loading && !open && setOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    if (!open) setOpen(true);
                    setHoveredIndex((prev) => {
                      const max = (filteredValues?.length ?? 1) - 1;
                      return prev === null || prev >= max ? 0 : prev + 1;
                    });
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (!open) setOpen(true);
                    setHoveredIndex((prev) => {
                      const max = (filteredValues?.length ?? 1) - 1;
                      return prev === null || prev <= 0 ? max : prev - 1;
                    });
                  } else if (e.key === 'Enter') {
                    if (!open) return;
                    e.preventDefault();
                    const idx = hoveredIndex !== null ? hoveredIndex : 0;
                    const item = filteredValues?.[idx];
                    if (item?.value !== undefined && item.value !== null) {
                      onSelectedItemChange(item.value);
                      setOpen(false);
                      setQuery('');
                    }
                  } else if (e.key === 'Escape') {
                    setOpen(false);
                    setQuery('');
                  }
                  e.stopPropagation();
                }}
                id={id}
                className={`combobox block w-full rounded-lg border border-neutral-100 bg-neutral-100 p-2 text-sm text-neutral-900 ${
                  disabled ? 'disabled' : 'focus:border focus:border-therablue focus:ring-therablue'
                } ${icon ? 'pl-14' : ''} ${showClear ? 'pr-16' : ''}`}
                onChange={(event) => {
                  setQuery(event.target.value);
                  if (!open) setOpen(true);
                }}
                value={inputDisplayValue}
                name={name}
                ref={ref}
                required={required}
                placeholder={placeholder}
                autoComplete={autoComplete}
                readOnly={disabled || loading}
                role="combobox"
                aria-expanded={open}
                aria-controls="dropdown-list"
              />
              <div className="absolute inset-y-0 right-0 flex items-center gap-1 rounded-r-md pr-4 focus:outline-none">
                {showClear && (
                  <button
                    type="button"
                    aria-label="Clear selection"
                    // The popover is `modal`, so while the list is open Radix's dismiss layer
                    // puts `pointer-events: none` on <body> and this button — which lives in the
                    // anchor, not the content — stops being a pointer target entirely. Opting back
                    // in lets it receive the event; handling clear on pointerdown then makes it
                    // independent of whether dismissal lands before the click.
                    className="pointer-events-auto flex items-center hover:cursor-pointer"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSelectedItemChange('');
                      setQuery('');
                      setOpen(false);
                    }}
                    // pointerdown alone leaves keyboard users without the control.
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      e.stopPropagation();
                      onSelectedItemChange('');
                      setQuery('');
                      setOpen(false);
                    }}
                  >
                    <XMarkIcon className="h-4 w-4 text-neutral-500" aria-hidden="true" />
                  </button>
                )}
                <div
                  className={`flex items-center pl-1 ${disabled ? '' : 'hover:cursor-pointer'}`}
                  onClick={() => {
                    if (!disabled && !loading) {
                      setOpen(!open);
                      if (open) setQuery('');
                    }
                  }}
                >
                  <ChevronDownIcon className="h-4 w-4 text-neutral-500" aria-hidden="true" />
                </div>
              </div>
            </div>
          </PopoverPrimitive.Anchor>
        </div>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            side={position === 'top' ? 'top' : 'bottom'}
            align="start"
            sideOffset={4}
            avoidCollisions
            onOpenAutoFocus={(e) => {
              // Prevent Radix from moving focus to the popover — keep focus on the input
              e.preventDefault();
            }}
            onCloseAutoFocus={(e) => {
              // Prevent Radix from refocusing the trigger on close
              e.preventDefault();
            }}
            className="z-[9999] origin-[--radix-popover-content-transform-origin] overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 duration-150 animate-in fade-in-0 zoom-in-95 focus:outline-none"
            style={{ width: 'var(--radix-popover-trigger-width)' }}
          >
            <div
              ref={listRef}
              id="dropdown-list"
              role="listbox"
              className="max-h-60 overflow-auto overscroll-contain py-1 text-base md:text-sm"
              onScroll={lazyLoading ? handleScroll : undefined}
            >
              {buttonPosition === 'top' && <ButtonComponent position="top" />}
              {filteredValues?.map(({ label, value, additionalValue, isAddButton }, index) => {
                const focus = hoveredIndex === index;
                const selected = value === selectedItem;
                return (
                  <div
                    key={index}
                    role="option"
                    aria-selected={selected}
                    data-add-button={isAddButton ? 'true' : 'false'}
                    className={classNames(
                      'relative cursor-default select-none hover:cursor-pointer',
                      isAddButton ? 'hover:text-white' : 'py-2 pl-3 pr-9',
                      focus ? 'bg-therablue text-white' : 'text-neutral-900',
                    )}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onClick={() => {
                      if (value !== undefined && value !== null) {
                        // With `clearable`, re-picking the current option deselects it — the list is
                        // the only affordance users reach for when they want to undo a choice.
                        onSelectedItemChange(clearable && selected ? '' : value);
                        setOpen(false);
                        setQuery('');
                      }
                    }}
                  >
                    <div className="flex min-w-0 flex-1 items-baseline gap-2">
                      <span className={classNames(minWidthForOptionLabel, 'truncate', selected && 'font-semibold')}>
                        {typeof label === 'string' ? (label ?? value) : (label ?? value)}
                      </span>
                      {additionalValue && (
                        <span
                          className={classNames(
                            'min-w-0 truncate text-gray-500',
                            focus ? 'text-white' : 'text-gray-500',
                          )}
                        >
                          {additionalValue}
                        </span>
                      )}
                    </div>

                    {selected && (
                      <span
                        className={classNames(
                          'absolute inset-y-0 right-0 flex items-center pr-4',
                          focus ? 'text-white' : 'text-therablue',
                        )}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                );
              })}
              {buttonPosition === 'bottom' && <ButtonComponent position="bottom" />}
              {!hasResults && query !== '' && (
                <div className="relative cursor-default select-none px-4 py-2 text-neutral-700">Nothing found.</div>
              )}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  },
);

export default Dropdown;
